<template>
  <div>
    <div> <!-- Stats -->
      <b-collapse
        aria-id="contentForOnlineTerminals"
        class="card"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile">
            <div class="tile-icon" :class="{ 'has-background-success': onlineTerminals > 0, 'has-background-grey-light': onlineTerminals <= 0 }">
              <font-awesome-icon :icon="['far', 'satellite-dish']" size="lg"></font-awesome-icon>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Online Terminals</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{onlineTerminals}}</h3>
            </div>
          </div>
        </template>
        <div class="panel">
          <div class="panel-block">
            <span class="panel-icon">
              <font-awesome-icon :icon="['far', 'satellite-dish']"></font-awesome-icon>
            </span>
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>Satellite Online</span>
              <strong>{{ accessStats?.satelliteOnlineTerminals }}</strong>
            </span>
          </div>
          <div class="panel-block">
            <span class="panel-icon">
              <font-awesome-icon :icon="['far', 'broadcast-tower']"></font-awesome-icon>
            </span>
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>Cellular Online</span>
              <strong>{{ accessStats?.cellularOnlineTerminals }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
      <div class="kpt-stat-tile card mt-2">
        <div class="tile-icon" :class="{ 'has-background-warning': accessStats?.privateTerminals > 0, 'has-background-grey-light': accessStats?.privateTerminals <= 0 }">
          <font-awesome-icon :icon="['far', 'eye-slash']" size="lg"></font-awesome-icon>
        </div>
        <div class="tile-label px-4 py-3">
          <p class="is-unselectable">Private Terminals</p>
        </div>
        <div class="tile-value">
          <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
          <h3 v-if="!accessStatsLoading">{{accessStats?.privateTerminals}}</h3>
        </div>
      </div>
      <div class="kpt-stat-tile card mt-2" v-if="$hasRole('Super User,Kymeta Admin')">
        <div class="tile-icon" :class="{ 'has-background-info': accessStats?.pausedTerminals > 0, 'has-background-grey-light': accessStats?.pausedTerminals <= 0 }">
          <font-awesome-icon :icon="['far', 'pause']" size="lg"></font-awesome-icon>
        </div>
        <div class="tile-label px-4 py-3">
          <p class="is-unselectable">Paused Terminals</p>
        </div>
        <div class="tile-value">
          <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
          <h3 v-if="!accessStatsLoading">{{accessStats?.pausedTerminals}}</h3>
        </div>
      </div>
      <b-collapse
        aria-id="contentForActiveSubscriptions"
        class="card mt-2"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile card">
            <div class="tile-icon" :class="{ 'has-background-success': accessStats?.activeSubscriptions > 0, 'has-background-grey-light': accessStats?.activeSubscriptions <= 0 }">
              <font-awesome-layers class="fa-lg">
                <font-awesome-icon :icon="['far', 'sync-alt']" />
                <font-awesome-icon :icon="['far', 'dollar-sign']" transform="shrink-8" :style="{ color: 'white' }" />
              </font-awesome-layers>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Active Subscriptions</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{accessStats?.activeSubscriptions}}</h3>
            </div>
          </div>
        </template>
        <div class="panel">
          <div class="panel-block" v-for="item in subscriptionBreakdown" :key="item.size">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>{{ item.size }}GB Data Package</span>
              <strong>{{ item.count }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
      <b-collapse
        aria-id="contentForBeams"
        class="card mt-2"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile card">
            <div class="tile-icon" :class="{ 'has-background-success': activeBeamCount > 0, 'has-background-grey-light': activeBeamCount <= 0 }">
              <font-awesome-icon :icon="['far', 'satellite']" size="lg"></font-awesome-icon>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Active Beams</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{activeBeamCount}}</h3>
            </div>
          </div>
        </template>
        <div class="panel" v-if="accessStats?.beamStats">
          <div class="panel-block" v-for="(value, key) in accessStats.beamStats" :key="key">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>{{ key }}</span>
              <strong>{{ value }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
      <b-collapse
        aria-id="contentForSoftware"
        class="card mt-2"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile card">
            <div class="tile-icon" :class="{ 'has-background-success': activeSoftwareCount > 0, 'has-background-grey-light': activeSoftwareCount <= 0 }">
              <font-awesome-icon :icon="['far', 'code']" size="lg"></font-awesome-icon>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Active Software</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{activeSoftwareCount}}</h3>
            </div>
          </div>
        </template>
        <div class="panel" v-if="accessStats?.softwareStats">
          <div class="panel-block" v-for="(value, key) in accessStats.softwareStats" :key="key">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>{{ key }}</span>
              <strong>{{ value }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
    </div>
    <div v-if="$hasRole('Super User,Kymeta Admin')"> <!-- Kymeta Stats -->
      <b-collapse
        aria-id="contentForSoftware"
        class="card mt-2"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile card">
            <div class="tile-icon has-background-info">
              <font-awesome-icon :icon="['far', 'users']" size="lg"></font-awesome-icon>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Monthly Active Users</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{monthlyActiveUsers}}</h3>
            </div>
          </div>
        </template>
        <div class="panel">
          <div class="panel-block">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>Kymeta Users</span>
              <strong>{{ accessStats?.monthlyActiveKymetaUsers }}</strong>
            </span>
          </div>
          <div class="panel-block">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>External Users</span>
              <strong>{{ accessStats?.monthlyActiveNonKymetaUsers }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
      <b-collapse
        aria-id="contentForSoftware"
        class="card mt-2"
        :open="false"
        animation="slide">
        <template #trigger>
          <div class="kpt-stat-tile card">
            <div class="tile-icon has-background-info">
              <font-awesome-icon :icon="['far', 'building']" size="lg"></font-awesome-icon>
            </div>
            <div class="tile-label px-4 py-3">
              <p class="is-unselectable">Accounts</p>
            </div>
            <div class="tile-value">
              <b-skeleton height="16px" :active="accessStatsLoading"></b-skeleton>
              <h3 v-if="!accessStatsLoading">{{accessStats?.totalAccounts}}</h3>
            </div>
          </div>
        </template>
        <div class="panel">
          <div class="panel-block">
            <span class="is-flex is-justify-content-space-between is-flex-grow-1">
              <span>Accounts w/ Configurator Access</span>
              <strong>{{ accessStats?.accountsWithConfiguratorAccess }}</strong>
            </span>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted } from 'vue';
import { useHomeStore } from '../../home-store';

const homeStore = useHomeStore();

const accessStats = computed(() => homeStore.state.accessStats);
const accessStatsLoading = computed(() => homeStore.state.accessStatsLoading);
const onlineTerminals = computed(() => (accessStats?.value?.satelliteOnlineTerminals || 0) + (accessStats?.value?.cellularOnlineTerminals || 0));
const subscriptionBreakdown = computed(() => {
  if (!accessStats?.value?.activeSubscriptionsBreakdown) return [];
  return Object.keys(accessStats?.value?.activeSubscriptionsBreakdown).map(key => ({ size: key, count: accessStats.value.activeSubscriptionsBreakdown[key] }));
});
const activeBeamCount = computed(() => (accessStats?.value?.beamStats ? Object.keys(accessStats?.value?.beamStats).length : 0));
const activeSoftwareCount = computed(() => (accessStats?.value?.softwareStats ? Object.keys(accessStats?.value?.softwareStats).length : 0));
const monthlyActiveUsers = computed(() => (accessStats?.value?.monthlyActiveKymetaUsers || 0) + (accessStats?.value?.monthlyActiveNonKymetaUsers || 0));

onMounted(async () => {
  await homeStore.dispatch('fetchAccessStats');
});
</script>
<style lang="scss">
@import '../../../styles/colors';

.kpt-stat-tile {
  display: flex;
  .tile-icon {
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $white;
    min-width: 52px;
  }
  .tile-label {
    flex: 1 0 auto;
  }
  .tile-value {
    padding-right: 0.5em;
    display: grid; 
    place-items: center;
  }
}
</style>
